import React, { useEffect, useState } from 'react';
import {
    Radar,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    ResponsiveContainer,
    Tooltip
} from 'recharts';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { swotAnalysisService, SwotData } from '../services/SwotAnalysisService';

interface FormattedSwotData {
    subject: string;
    value: number;
    fullMark: number;
}

const SwotRadarChart: React.FC = () => {
    const [swotData, setSwotData] = useState<SwotData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [maxDomainValue, setMaxDomainValue] = useState<number>(300);

    useEffect(() => {
        const fetchSwotData = async () => {
            try {
                setLoading(true);
                const data = await swotAnalysisService.obterDadosSwot();
                setSwotData(data);

                // Calculate the maximum domain value based on the highest score
                if (data) {
                    const maxScore = Math.max(
                        data.pontosFortesPontuacaoTotal,
                        data.ameacasPontuacaoTotal,
                        data.pontosFracosPontuacaoTotal,
                        data.oportunidadesPontuacaoTotal
                    );

                    // Add 20% buffer to the max value for better visualization
                    const domainMax = Math.ceil(maxScore * 1.2);
                    setMaxDomainValue(domainMax);
                }

                setLoading(false);
            } catch (err) {
                console.error('Erro ao carregar dados SWOT:', err);
                setError('Não foi possível carregar os dados da análise SWOT');
                setLoading(false);
            }
        };

        fetchSwotData();
    }, []);

    // Formatar dados para o gráfico de radar
    const formatDataForRadarChart = (data: SwotData | null): FormattedSwotData[] => {
        if (!data) return [];

        return [
            { subject: 'Pontos Fortes', value: data.pontosFortesPontuacaoTotal, fullMark: maxDomainValue },
            { subject: 'Ameaças', value: data.ameacasPontuacaoTotal, fullMark: maxDomainValue },
            { subject: 'Pontos Fracos', value: data.pontosFracosPontuacaoTotal, fullMark: maxDomainValue },
            { subject: 'Oportunidades', value: data.oportunidadesPontuacaoTotal, fullMark: maxDomainValue }
        ];
    };

    if (loading) {
        return <Typography>Carregando análise SWOT...</Typography>;
    }

    if (error) {
        return <Typography color="error">{error}</Typography>;
    }

    const radarData = formatDataForRadarChart(swotData);

    return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
            {/* Tabela com a pontuação */}
            <TableContainer component={Paper} sx={{ maxWidth: 300 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Categoria</TableCell>
                            <TableCell align="right">Pontuação</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {swotData && (
                            <>
                                <TableRow>
                                    <TableCell>Pontos Fortes</TableCell>
                                    <TableCell align="right">{swotData.pontosFortesPontuacaoTotal}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Ameaças</TableCell>
                                    <TableCell align="right">{swotData.ameacasPontuacaoTotal}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Pontos Fracos</TableCell>
                                    <TableCell align="right">{swotData.pontosFracosPontuacaoTotal}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Oportunidades</TableCell>
                                    <TableCell align="right">{swotData.oportunidadesPontuacaoTotal}</TableCell>
                                </TableRow>
                            </>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Gráfico de Radar */}
            <Box
                sx={{
                    width: '100%',
                    height: 500,
                    maxWidth: 650,
                }}
            >
                <ResponsiveContainer width="100%" height="90%">
                    <RadarChart
                        outerRadius={150}
                        data={radarData}
                        margin={{ top: 20, right: 30, bottom: 20, left: 30 }}
                    >
                        <PolarGrid />
                        <PolarAngleAxis
                            dataKey="subject"
                            tick={{
                                fontSize: 12,
                                fontWeight: 'bold',
                                fill: '#666666',
                                dy: 5
                            }}
                            tickLine={false}
                        />
                        <PolarRadiusAxis
                            angle={90}
                            domain={[0, maxDomainValue]}
                            tick={false} // Hide the numeric labels
                            tickCount={5} // Still maintain grid lines
                            axisLine={false} // Hide the axis line
                        />
                        <Radar
                            name="Pontuação"
                            dataKey="value"
                            stroke="#2463E9"
                            fill="#2463E9"
                            fillOpacity={0.6}
                        />
                        <Tooltip formatter={(value) => [`${value}`, 'Pontuação']} />
                    </RadarChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
};

export default SwotRadarChart;
